import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Save, Trash2 } from "lucide-react";
import { adminGetTracking, adminSaveTracking } from "@/lib/api";

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 font-mono text-xs text-white placeholder-zinc-600 outline-none transition-colors focus:border-brand-yellow";

const AdminTracking = ({ adminKey }) => {
  const [code, setCode] = useState("");
  const [loaded, setLoaded] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = () =>
    adminGetTracking(adminKey)
      .then(setLoaded)
      .catch(() => toast.error("Could not load tracking settings"));

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminSaveTracking({ pixel_code: code }, adminKey);
      toast.success(
        res.pixel_id ? "Meta Pixel activated" : "Tracking disabled",
        { description: res.pixel_id ? `Pixel ID ${res.pixel_id} is now live site-wide` : "Pixel removed from the storefront" }
      );
      setCode("");
      refresh();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setSaving(false);
    }
  };

  const disable = async () => {
    if (!window.confirm("Remove the Meta Pixel from the storefront? Tracking stops immediately.")) return;
    setSaving(true);
    try {
      await adminSaveTracking({ pixel_code: "" }, adminKey);
      toast.success("Tracking disabled");
      refresh();
    } catch {
      toast.error("Could not disable");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-tracking-panel" className="max-w-xl border border-white/10 bg-ink-3">
      <div className="h-1 cmyk-line" />
      <form onSubmit={save} className="p-6">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-brand-cyan" />
          <div>
            <h3 className="font-display text-xl font-black uppercase text-white">Meta Pixel</h3>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Page views • Add to Cart • Purchase events
            </p>
          </div>
        </div>

        {loaded && (
          <p
            data-testid="admin-tracking-status"
            className="mt-4 border border-white/10 bg-ink px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400"
          >
            {loaded.enabled ? (
              <>Active — Pixel ID <span className="text-brand-cyan">{loaded.pixel_id}</span></>
            ) : (
              "Not configured — no tracking running"
            )}
          </p>
        )}

        <label className="mt-5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Meta Pixel Code (paste the full snippet from Events Manager, or just the Pixel ID)
        </label>
        <textarea
          data-testid="admin-tracking-code"
          rows={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="<!-- Meta Pixel Code --> … fbq('init', '1234567890123456'); … or just: 1234567890123456"
          className={`${inputCls} mt-1.5`}
        />

        <button
          data-testid="admin-tracking-save"
          type="submit"
          disabled={saving || !code.trim()}
          className="mt-5 flex w-full items-center justify-center gap-2 bg-brand-yellow py-3.5 font-display text-sm font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:opacity-40"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Activate Pixel"}
        </button>
        {loaded?.enabled && (
          <button
            type="button"
            data-testid="admin-tracking-disable"
            onClick={disable}
            disabled={saving}
            className="mt-3 flex w-full items-center justify-center gap-2 border border-brand-magenta/50 py-3 font-display text-xs font-bold uppercase tracking-widest text-brand-magenta transition-colors hover:bg-brand-magenta hover:text-white disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove Pixel
          </button>
        )}
        <p className="mt-3 font-mono text-[9px] uppercase leading-relaxed tracking-wider text-zinc-600">
          Only the numeric Pixel ID is stored — the storefront injects Meta's official snippet.
          Events fire on page views, Add to Cart, and completed checkouts.
        </p>
      </form>
    </div>
  );
};

export default AdminTracking;
